<script setup>
  import { ref, computed, watchEffect, defineAsyncComponent } from 'vue';
  import TideSpotModalSelector from '@/components/TideSpotModalSelector.vue';
  import BaseModal from '@/components/base/BaseModal.vue';
  import SegmentedControl from '@/components/controls/SegmentedControl.vue';
  import SegmentedControlButton from '@/components/controls/SegmentedControlButton.vue';

  const NearbyTideSpotMap = defineAsyncComponent(() => import('@/components/NearbyTideSpotMap.vue'));

  const props = defineProps({
    spot: {
      type: Object,
      required: true
    },
    nearbyTideSpots: {
      type: Array,
      required: true
    },
    selectedTideSpot: {
      type: Object,
      required: true
    },
    hasCurrentsSpot: {
      type: Boolean,
      required: true
    }
  });

  const showModal = defineModel();
  const emits = defineEmits(['selected:tideSpot']);

  const spotSearchRadius = ref(null);
  const spotSearchType = ref('all');
  const selectedTideSpot = ref(props.selectedTideSpot);

  const tideSpotsWithinDistance = computed(() => props.nearbyTideSpots.filter(spot => spot.distance <= spotSearchRadius.value));
  const filteredTideSpots = computed(() => spotSearchType.value === 'all' ? tideSpotsWithinDistance.value : tideSpotsWithinDistance.value.filter(spot => spot.type === spotSearchType.value));

  const distanceControlInitialValue = computed(() => {
    return props.selectedTideSpot.distance > 5 && props.selectedTideSpot.distance <= 10 ? '10' : 
           props.selectedTideSpot.distance > 10 && props.selectedTideSpot.distance <= 15 ? '15' :
           props.selectedTideSpot.distance > 15 && props.selectedTideSpot.distance <= 20 ? '20' : '5';
  });

  const handleTideSpotSelection = (spot) => {
    emits('selected:tideSpot', spot.id);
  };

  watchEffect(() => {
    selectedTideSpot.value = props.selectedTideSpot;
  })
</script>

<template>
  <BaseModal
    v-model="showModal"
    height="full"
    width="x-wide"
  >
  <div class="jwx-modal__header">
    <h3 class="jwx-modal__title">Choose a Nearby Tide or Current Spot</h3>
  </div>
  <div class="jwx-modal__body" data-columns="2" style="--jwx-modal-small-device-column-1-height: 200px;">
    <div class="jwx-modal__column" style="--jwx-modal-section-padding: 0; z-index: 2;">
      <NearbyTideSpotMap
        v-if="showModal"
        :spot="spot"
        :filteredTideSpots="filteredTideSpots"
        :selectedTideSpot="selectedTideSpot"
        @selected:tideSpot="handleTideSpotSelection"
      />
    </div>
    <div class="jwx-modal__column">
      <div class="jwx-modal__section" data-sticky>
        <div class="jwx-modal__section-actions w-100 jwx-justify-between">
          <SegmentedControl
            v-if="props.hasCurrentsSpot"
            v-model="spotSearchType"
            defaultValue="'all'"
            label="Tide/Current Spot Search Type"
          >
            <SegmentedControlButton :allowLineBreaks="false" value="all">All</SegmentedControlButton>
            <SegmentedControlButton :allowLineBreaks="false" value="tide">Tide</SegmentedControlButton>
            <SegmentedControlButton :allowLineBreaks="false" value="currents">Currents</SegmentedControlButton>
          </SegmentedControl>
          <SegmentedControl
            v-model="spotSearchRadius"
            :defaultValue="distanceControlInitialValue"
            label="Tide/Current Spot Search Radius"
          >
            <SegmentedControlButton :allowLineBreaks="false" value="5">5 mi</SegmentedControlButton>
            <SegmentedControlButton :allowLineBreaks="false" value="10">10 mi</SegmentedControlButton>
            <SegmentedControlButton :allowLineBreaks="false" value="15">15 mi</SegmentedControlButton>
            <SegmentedControlButton :allowLineBreaks="false" value="20">20 mi</SegmentedControlButton>
          </SegmentedControl>
        </div>
      </div>
      <div class="jwx-modal__section jwx-flex jwx-flex-column">
        <div v-if="filteredTideSpots.length === 0" class="jwx-flex jwx-text-center jwx-justify-center jwx-align-center" style="height: 100%;">
          <p class="jwx-text jwx-font-size-lg">There are no nearby tide or current spots found within the selected distance.</p>
        </div>
        
        <ul v-else class="jwx-table-view" data-grouped="true">
          <li
            v-for="spot in filteredTideSpots"
            class="jwx-table-view__row" 
            :key="spot.id"
          >
            <TideSpotModalSelector
              :tideSpot="spot"
              :isSelected="spot.id === selectedTideSpot.id"
              @selected:tideSpot="handleTideSpotSelection"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div class="jwx-modal__footer">
    <div class="jwx-modal__actions">
      <button 
        type="button" 
        @click.prevent="showModal = false"
        class="jwx-button jwx-button--success"
      >
        Done
      </button>
    </div>
  </div>  
  </BaseModal>
</template>